import {
  BillingAddonReferenceFragment,
  CheckSubscriptionItemsInstalledQuery,
  GetEnabledModulesQuery,
  MarketplaceModulesDataCollectionQuery,
} from 'generated/resources/graphql'

import { Company } from 'modules/core/hooks/useCurrentSession'

import getF25Plans from 'modules/upsell/lib/selfUpsell/plans/getF25Plans'
import {
  BUSINESS_VARIANT,
  ENTERPRISE_VARIANT,
  getCountryFromPlan,
  mapToF25Addon,
} from 'modules/upsell/lib/selfUpsell/plans/utils'

type ModuleFromQuery = NonNullable<
  MarketplaceModulesDataCollectionQuery['modules']
>['datoModulesConnection']['nodes'][number]

type IncludedInPlanFromQuery = NonNullable<ModuleFromQuery['includedInPlan']>[number]

type SubscriptionItems =
  CheckSubscriptionItemsInstalledQuery['billing']['subscriptionItemsConnection']['nodes']

type ActiveSubscriptionOverload =
  | GetEnabledModulesQuery['companies']['subscriptionOverloadsConnection']['nodes'][number]
  | null

export type ModuleStatus = {
  isInstalled: boolean
  variant: typeof BUSINESS_VARIANT | typeof ENTERPRISE_VARIANT | null
  installedAddonInternalId: string | null
  from: 'subscription' | 'bundle' | 'overload' | null
  hasEnterpriseVariant?: boolean | undefined
}

export type ModuleIncludedInPlanData = Array<{
  moduleName: string
  includedInPlan: IncludedInPlanFromQuery[]
}>

export type ModuleIncludedInPlanDataFlexible = Array<{
  moduleName: string
  includedInPlan: Array<{
    regions: Array<{
      chargebeePlans: Array<{ planId: string }>
    }>
  }>
}>

/**
 * Checks if a module is installed via bundle/plan inclusion
 *
 * A module is considered installed via bundle if:
 * 1. The module has plan inclusion data (includedInPlan)
 * 2. Any of the plan IDs from the module's included plans match
 *    the addon IDs in the user's current subscription items
 *
 * @param moduleName - The name of the module to check
 * @param moduleIncludedInPlan - Array of modules with their plan inclusion data
 * @param subscriptionItems - Array of subscription items from the user's current subscription
 * @returns Status object with isInstalled flag, variant (currently null for bundles), and installedAddonInternalId (subscription item addon ID, e.g., "f25_shifts_b-month-es-eur")
 *
 * Note: Bundle status doesn't currently determine variant (business/enterprise)
 * This could be enhanced in the future by checking plan variant information
 */
const getModuleBundleStatus = (
  moduleName: string | null | undefined,
  moduleIncludedInPlan: ModuleIncludedInPlanData | ModuleIncludedInPlanDataFlexible,
  subscriptionItems: SubscriptionItems,
  isEnterpriseVariantAvailable: boolean
): ModuleStatus => {
  // Early return if module has no name or no plan data available
  if (!moduleName || moduleIncludedInPlan.length === 0) {
    return { isInstalled: false, variant: null, installedAddonInternalId: null, from: 'bundle' }
  }

  // Find the module's plan inclusion data by matching module name
  const modulePlanData = moduleIncludedInPlan.find((m) => m.moduleName === moduleName)

  if (!modulePlanData) {
    return { isInstalled: false, variant: null, installedAddonInternalId: null, from: 'bundle' }
  }

  // Check each plan that includes this module
  for (const plan of modulePlanData.includedInPlan) {
    // Extract all plan IDs from all regions and chargebee plans
    // This gives us a flat list of all plan IDs that include this module
    const planIds = plan.regions.flatMap((region) => region.chargebeePlans.map((p) => p.planId))
    // Check each subscription item to see if it matches any of the plan IDs
    for (const subscriptionItem of subscriptionItems) {
      // Find the matching addon ID that corresponds to a plan ID
      const matchingItemId = subscriptionItem.itemIds?.find((itemId) =>
        planIds.some((planId) => itemId === planId)
      )

      if (matchingItemId) {
        // Module is installed via bundle - found a matching plan ID
        // Note: Variant is null because bundle status doesn't currently track variant
        // This could be enhanced by checking the plan's variant information
        return {
          isInstalled: true,
          variant: getVariantFromAddonId(matchingItemId, isEnterpriseVariantAvailable),
          installedAddonInternalId: matchingItemId,
          from: 'bundle',
        }
      }
    }
  }

  return { isInstalled: false, variant: null, installedAddonInternalId: null, from: 'bundle' }
}

/**
 * Checks if a module is installed via direct subscription (addon purchase)
 *
 * Uses the moduleStatusMap which is pre-populated by useCheckSubscriptionItemsInstalled hook
 * This map contains installation status for all modules with billing addon references
 *
 * @param billingAddonReference - The billing addon reference array from the module
 * @param moduleStatusMap - Map of module statuses keyed by internalTitle
 * @returns Status object with isInstalled flag, variant (business/enterprise/null), and installedAddonInternalId (subscription item addon ID, e.g., "f25_shifts_b-month-es-eur")
 */
const getModuleSubscriptionStatus = (
  billingAddonReference: BillingAddonReferenceFragment[] | null | undefined,
  moduleStatusMap: Map<string, ModuleStatus> | undefined
): ModuleStatus => {
  // Modules without billing addon references can't be installed via subscription
  if (!billingAddonReference || billingAddonReference.length === 0) {
    return {
      isInstalled: false,
      variant: null,
      installedAddonInternalId: null,
      from: 'subscription',
    }
  }

  // Use the first addon reference's internalTitle as the lookup key
  // The moduleStatusMap is keyed by internalTitle
  const key = billingAddonReference[0]?.internalTitle || ''
  const status = moduleStatusMap?.get(key)

  if (status) {
    // Return the status - installedAddonInternalId is already set from the hook
    // which contains the actual subscription item addon ID (e.g., "f25_shifts_b-month-es-eur")
    return status
  }

  return { isInstalled: false, variant: null, installedAddonInternalId: null, from: 'subscription' }
}

/**
 * Checks if a module is installed via subscription overload (trial/overload)
 *
 * A module is considered installed via subscription overload if:
 * 1. There is an active subscription overload
 * 2. The subscription overload's addon IDs match any of the addon IDs in subscription items
 *
 * @param subscriptionItems - Array of subscription items from the user's current subscription
 * @param activeSubscriptionOverload - The active subscription overload from the query
 * @returns Status object with isInstalled flag, variant (null for overloads), and installedAddonInternalId (matching addon ID)
 */
const getActiveSubscriptionOverload = (
  billingAddonReference: BillingAddonReferenceFragment[] | null | undefined,
  activeSubscriptionOverload: ActiveSubscriptionOverload,
  isEnterpriseVariantAvailable: boolean
): ModuleStatus => {
  // Early return if no active subscription overload

  if (!activeSubscriptionOverload || !activeSubscriptionOverload.addonIds) {
    return { isInstalled: false, variant: null, installedAddonInternalId: null, from: 'overload' }
  }

  if (!billingAddonReference || billingAddonReference.length === 0) {
    return { isInstalled: false, variant: null, installedAddonInternalId: null, from: 'overload' }
  }

  const overloadAddonIds = activeSubscriptionOverload.addonIds

  // Check if any overload addon ID matches any billing addon reference addon ID
  // Overload addon IDs have format: f25_module_name_variant-country
  // Billing addon IDs have format: f25_module_name_variant-period-country-currency
  // Example: f25_project_management_e-es matches f25_project_management_e-year-es-eur
  for (const overloadAddonId of overloadAddonIds) {
    // Extract the base part (before the last dash, which is the country code)
    // For f25_project_management_e-es, base is f25_project_management_e
    const lastDashIndex = overloadAddonId.lastIndexOf('-')
    const basePart =
      lastDashIndex > 0 ? overloadAddonId.substring(0, lastDashIndex) : overloadAddonId
    const countryCode = lastDashIndex > 0 ? overloadAddonId.substring(lastDashIndex + 1) : ''

    // Find matching addon ID in billing reference
    for (const billingRef of billingAddonReference) {
      for (const region of billingRef.regions) {
        for (const chargebeeAddon of region.chargebeeAddons) {
          // Check if billing addon ID matches the pattern: {base}-{period}-{country}-...
          // Where period is -year- or -month-
          // Example: f25_project_management_e-year-es-eur matches f25_project_management_e-es
          if (chargebeeAddon.addonId.startsWith(basePart + '-')) {
            // Check if it has a period (-year- or -month-) followed by the country code
            const match = chargebeeAddon.addonId.match(
              new RegExp(
                `^${basePart.replace(
                  /[.*+?^${}()|[\]\\]/g,
                  '\\$&'
                )}-(year|month)-${countryCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`
              )
            )

            if (match) {
              // Determine variant from the overload addon ID pattern
              const variant = getVariantFromAddonId(overloadAddonId, isEnterpriseVariantAvailable)
              return {
                isInstalled: true,
                variant,
                installedAddonInternalId: chargebeeAddon.addonId,
                from: 'overload',
              }
            }
          }
        }
      }
    }
  }

  return { isInstalled: false, variant: null, installedAddonInternalId: null, from: 'overload' }
}

const getVariantFromAddonId = (
  addonId: string,
  isEnterpriseVariantAvailable: boolean
): typeof BUSINESS_VARIANT | typeof ENTERPRISE_VARIANT | null => {
  if (addonId.endsWith('_b') || addonId.includes('_b-')) {
    return BUSINESS_VARIANT
  }
  if (addonId.endsWith('_e') || (addonId.includes('_e-') && isEnterpriseVariantAvailable)) {
    return ENTERPRISE_VARIANT
  }
  if (addonId.endsWith('_e') || (addonId.includes('_e-') && !isEnterpriseVariantAvailable)) {
    return BUSINESS_VARIANT
  }

  return null
}

/**
 * Checks if a module has an enterprise variant available
 *
 * @param addonRef - The billing addon reference array from the module
 * @param country - The country code to check plans for
 * @returns true if the module has an enterprise variant, false otherwise
 */
export const hasEnterpriseVariant = (
  addonRef: BillingAddonReferenceFragment[],
  country: string
): boolean => {
  const plans = getF25Plans({ allF25Addons: mapToF25Addon(addonRef) }, country).f25Addons
  return plans.some((plan) => plan.getSpecificVariant(ENTERPRISE_VARIANT) != null)
}

/**
 * Gets the complete installation status for a module
 *
 * Checks installation status in priority order:
 * 1. Subscription overload status (trial/overload) - highest priority, checked first
 * 2. Subscription status (direct addon purchase) - checked if not found in overload
 * 3. Bundle status (included in plan) - checked if not found in overload or subscription
 *
 * Subscription overload has the highest priority as it represents active trials/overloads
 * that should be displayed over regular subscription or bundle installations.
 *
 */
export const getModuleStatus = ({
  company,
  moduleName,
  billingAddonReference,
  moduleStatusMap,
  moduleIncludedInPlan,
  subscriptionItems,
  activeSubscriptionOverload,
}: {
  company: Company
  moduleName: string | null | undefined
  billingAddonReference: BillingAddonReferenceFragment[] | null | undefined
  moduleStatusMap: Map<string, ModuleStatus> | undefined
  moduleIncludedInPlan: ModuleIncludedInPlanData | ModuleIncludedInPlanDataFlexible
  subscriptionItems: SubscriptionItems
  activeSubscriptionOverload?: ActiveSubscriptionOverload
}): ModuleStatus => {
  const legalEntityCountry = company.legalEntities.find((entity) => entity.main)?.country || ''
  const country = getCountryFromPlan(company.subscription.plan, legalEntityCountry)
  const isEnterpriseVariantAvailable = billingAddonReference
    ? hasEnterpriseVariant(billingAddonReference, country)
    : false

  // Priority 1: Check subscription overload status (trial/overload) - highest priority
  const overloadStatus = getActiveSubscriptionOverload(
    billingAddonReference,
    activeSubscriptionOverload || null,
    isEnterpriseVariantAvailable
  )

  if (overloadStatus.isInstalled) {
    return { ...overloadStatus, hasEnterpriseVariant: isEnterpriseVariantAvailable }
  }

  // Priority 2: Check subscription status (direct addon purchase)
  // Only checked if not found in overload
  const subscriptionStatus = getModuleSubscriptionStatus(billingAddonReference, moduleStatusMap)

  // If installed via subscription, return that status (includes variant)
  if (subscriptionStatus.isInstalled) {
    return { ...subscriptionStatus, hasEnterpriseVariant: isEnterpriseVariantAvailable }
  }

  // Priority 3: Check bundle status (included in plan)
  // Only checked if not found in overload or subscription
  const bundleStatus = getModuleBundleStatus(
    moduleName,
    moduleIncludedInPlan,
    subscriptionItems,
    isEnterpriseVariantAvailable
  )

  return { ...bundleStatus, hasEnterpriseVariant: isEnterpriseVariantAvailable }
}
